import { useId, useState, type ReactNode } from 'react'
import { ChevronDown, Globe, Mail, Plus, Store } from 'lucide-react'
import { Checkbox, SwitchRow } from '../admin/form.js'
import { VERROU_LIBELLE, type Canal } from './soirees-maison-model.js'
import {
  avecDebut, debutDeVente, nouvelleDateHeure, saisonDeLaValeur, valeurChampDateHeure,
  type ChampTarif, type DebutDeVente, type ErreursTarif, type SaisieTarif,
} from './tarif-saisie-model.js'

/**
 * LES CHAMPS D'UN TARIF, DANS LA FENÊTRE DE LA FICHE — rangés comme
 * l'édition d'un tarif chez Shotgun (« inspire-toi de Shotgun », 23/10/2026) :
 * où il se vend, l'essentiel (nom, prix, TVA, nombre de billets), la période
 * de vente en trois choix — dès la mise en vente, à une date précise, après
 * un autre tarif (la cascade) —, puis les options avancées, repliées, en
 * cartes à interrupteur.
 *
 * Mêmes champs et mêmes règles que `TarifChamps` (l'assistant), seulement
 * présentés autrement : la saisie, ses contrôles et le corps envoyé viennent
 * du même modèle (`tarif-saisie-model`). Aucun bouton « Enregistrer » ici :
 * c'est la barre d'enregistrement de Gestion qui enregistre (règle gardée par
 * l'exploitant, même jour).
 *
 * Ce qui ne se change plus après le premier billet (`verrous`) reste AFFICHÉ,
 * grisé, avec sa raison. Les options avancées s'ouvrent d'elles-mêmes quand
 * l'une d'elles est déjà posée ou signale une erreur : rien n'est caché.
 */

const CANAUX_FENETRE: { canal: Canal; titre: string; aide: string; icone: ReactNode }[] = [
  { canal: 'EN_LIGNE', titre: 'En ligne', aide: 'Sur la page de la soirée', icone: <Globe size={18} aria-hidden="true" /> },
  { canal: 'SUR_PLACE', titre: 'Sur place', aide: 'Vendu à la caisse', icone: <Store size={18} aria-hidden="true" /> },
  { canal: 'INVITATION', titre: 'Invitation', aide: 'Gratuite, nominative', icone: <Mail size={18} aria-hidden="true" /> },
]

const DEBUTS: { valeur: DebutDeVente; libelle: string }[] = [
  { valeur: 'mise', libelle: 'Dès la mise en vente' },
  { valeur: 'date', libelle: 'À une date précise' },
  { valeur: 'apres', libelle: 'Après un autre tarif' },
]

const VERROU_DU_CHAMP: Partial<Record<ChampTarif, string>> = {
  prix: 'PRIX', frais: 'FRAIS', canal: 'CANAL', exoneration: 'EXONERATION', tva: 'TVA',
}

const AVANCES: readonly ChampTarif[] = ['entree_avant', 'salles', 'consigne_porte', 'exoneration', 'cache', 'ordre', 'description']

function Champ(props: { id: string; label: string; aide?: string | null | undefined; erreur?: string | undefined; verrou?: string | null | undefined; children: ReactNode }) {
  return (
    <div className={`lc-field bs-champ${props.erreur ? ' bs-champ--erreur' : ''}`}>
      <label className="lc-label" htmlFor={props.id}>{props.label}</label>
      {props.children}
      {props.erreur ? <span className="lc-helper lc-helper--error" role="alert">{props.erreur}</span>
        : props.verrou ? <span className="lc-helper bs-champ__verrou">{props.verrou}</span>
          : props.aide ? <span className="lc-helper">{props.aide}</span> : null}
    </div>
  )
}

export function TarifFenetreChamps(props: {
  valeur: SaisieTarif
  onChange: (s: SaisieTarif) => void
  mode: 'creation' | 'modification'
  salles: readonly { code: string; nom: string }[]
  /** Les cascades que ce tarif peut rejoindre (`cascadesARejoindre`). */
  cascades: readonly { groupe: string; rang: number; noms: string[] }[]
  erreurs?: ErreursTarif | undefined
  verrous?: readonly string[] | undefined
  raisonsVerrous?: Readonly<Record<string, string>> | undefined
  tvaDefaut?: string | null | undefined
  quotaMin?: number | null | undefined
  complet?: boolean | undefined
  lectureSeule?: boolean | undefined
}) {
  const base = useId()
  const v = props.valeur
  const e = props.erreurs ?? {}
  const verrous = props.verrous ?? []
  const ro = props.lectureSeule === true
  const id = (k: string) => `${base}-${k}`
  const raison = (code: string): string => props.raisonsVerrous?.[code] ?? VERROU_LIBELLE[code] ?? 'Ce champ ne se change plus.'
  const fige = (c: ChampTarif): string | null => {
    const code = VERROU_DU_CHAMP[c]
    return code && verrous.includes(code) ? raison(code) : null
  }
  const set = <K extends ChampTarif>(k: K, x: SaisieTarif[K]) => props.onChange({ ...v, [k]: x })

  // Le choix du début et l'ouverture des options sont de la présentation : ils ne partent jamais.
  const [debut, setDebut] = useState<DebutDeVente>(() => debutDeVente(v))
  const [finOuverte, setFinOuverte] = useState(false)
  const [dateApresOuverte, setDateApresOuverte] = useState(false)
  const posees = v.entree_avant !== '' || v.salles.length > 0 || v.consigne_porte !== '' || v.cache || v.exoneration || v.description !== ''
  const [avancees, setAvancees] = useState(posees)
  const [ouvertes, setOuvertes] = useState<{ entree: boolean; salles: boolean; consigne: boolean }>({ entree: false, salles: false, consigne: false })
  const avanceesVisibles = avancees || AVANCES.some((k) => e[k])

  const changerCanal = (c: Canal) => {
    // Un tarif sur place ou une invitation n'a pas de frais ; l'exonération ne se fait que sur place.
    props.onChange({ ...v, canal: c, frais: c === 'EN_LIGNE' ? v.frais : '', exoneration: c === 'SUR_PLACE' ? v.exoneration : false })
  }
  const choisirDebut = (d: DebutDeVente) => { setDebut(d); props.onChange(avecDebut(v, d)) }

  const dateHeure = (k: 'vente_debut' | 'vente_fin', label: string, effacer: () => void) => {
    const saison = saisonDeLaValeur(v[k])
    return (
      <Champ id={id(k)} label={label} erreur={e[k]}
        aide={saison ? `Heure ${saison === 'hiver' ? 'd’hiver' : 'd’été'} : cette heure existe deux fois cette nuit-là.` : 'Heure de Paris.'}>
        <span className="bs-champ__ligne">
          <input id={id(k)} className="lc-input" type="datetime-local" step={60} disabled={ro}
            value={valeurChampDateHeure(v[k])} onChange={(ev) => set(k, nouvelleDateHeure(v[k], ev.target.value))} />
          {!ro ? <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={effacer}>Retirer</button> : null}
        </span>
      </Champ>
    )
  }

  /** Une carte d'option : son interrupteur, sa phrase, et ce qu'elle ouvre. */
  const carte = (p: { titre: string; desc: string; on: boolean; basculer: (on: boolean) => void; disabled?: boolean; children?: ReactNode }) => (
    <div className={`bs-tf__carte${p.on ? ' bs-tf__carte--on' : ''}`}>
      <SwitchRow checked={p.on} onChange={p.basculer} title={p.titre} desc={p.desc} disabled={ro || p.disabled === true} />
      {p.on && p.children ? <div className="bs-tf__carte-corps">{p.children}</div> : null}
    </div>
  )

  const entreeOn = v.entree_avant !== '' || ouvertes.entree
  const sallesOn = v.salles.length > 0 || ouvertes.salles
  const consigneOn = v.consigne_porte !== '' || ouvertes.consigne

  return (
    <div className="bs-tf">
      <section className="bs-tf__section" aria-labelledby={id('t-canal')}>
        <h3 className="bs-tf__titre" id={id('t-canal')}>Où se vend-il ?</h3>
        <div className="bs-tf__canaux" role="group" aria-label="Canal de vente">
          {CANAUX_FENETRE.map((c) => {
            const on = v.canal === c.canal
            const bloque = ro || (!!fige('canal') && !on)
            return (
              <button key={c.canal} type="button" className={`bs-tf__canal${on ? ' bs-tf__canal--on' : ''}`} aria-pressed={on}
                disabled={bloque} onClick={() => { if (!on) changerCanal(c.canal) }}>
                <span className="bs-tf__canal-icone">{c.icone}</span>
                <strong>{c.titre}</strong>
                <span className="bs-tf__canal-aide">{c.aide}</span>
              </button>
            )
          })}
        </div>
        {e.canal ? <span className="lc-helper lc-helper--error" role="alert">{e.canal}</span>
          : fige('canal') ? <span className="lc-helper bs-champ__verrou">{fige('canal')}</span>
            : <span className="lc-helper">{v.canal === 'SUR_PLACE' ? 'Vendu à la caisse de la porte.' : v.canal === 'EN_LIGNE' ? 'Pendant la démo, rien ne vend en ligne.' : 'Gratuit ; aucun écran n’émet encore d’invitation.'}</span>}
      </section>

      <section className="bs-tf__section" aria-labelledby={id('t-essentiel')}>
        <h3 className="bs-tf__titre" id={id('t-essentiel')}>L’essentiel</h3>
        <Champ id={id('nom')} label="Nom du tarif" erreur={e.nom} aide="Tel qu’il s’affiche sur le billet et à la caisse.">
          <input id={id('nom')} className="lc-input" value={v.nom} maxLength={80} disabled={ro} onChange={(ev) => set('nom', ev.target.value)} />
        </Champ>
        <div className="bs-tf__grille">
          <Champ id={id('prix')} label="Prix du billet (€)" erreur={e.prix} verrou={fige('prix')} aide={v.canal === 'INVITATION' ? 'Une invitation est gratuite : 0.' : 'Toutes taxes comprises.'}>
            <input id={id('prix')} className="lc-input" inputMode="decimal" placeholder="à saisir" value={v.prix} disabled={ro || !!fige('prix')} onChange={(ev) => set('prix', ev.target.value)} />
          </Champ>
          {v.canal === 'EN_LIGNE' ? (
            <Champ id={id('frais')} label="Frais de service (€)" erreur={e.frais} verrou={fige('frais')} aide="Vide : aucun.">
              <input id={id('frais')} className="lc-input" inputMode="decimal" value={v.frais} disabled={ro || !!fige('frais')} onChange={(ev) => set('frais', ev.target.value)} />
            </Champ>
          ) : null}
          <Champ id={id('tva')} label="TVA (%)" erreur={e.tva} verrou={fige('tva')}
            aide={props.mode === 'creation' ? `Vide : ${props.tvaDefaut ?? 'le réglage de la billetterie'}. Figée au premier billet.` : 'Figée au premier billet émis.'}>
            <input id={id('tva')} className="lc-input" inputMode="decimal" placeholder={props.mode === 'creation' ? (props.tvaDefaut ?? 'réglage par défaut') : ''} value={v.tva} disabled={ro || !!fige('tva')} onChange={(ev) => set('tva', ev.target.value)} />
          </Champ>
          <Champ id={id('quota')} label="Nombre de billets" erreur={e.quota}
            aide={typeof props.quotaMin === 'number' && props.quotaMin > 0 ? `Pas moins que les ${props.quotaMin} déjà vendus. Vide : sans limite.` : 'Vide : sans limite (la jauge reste la limite).'}>
            <input id={id('quota')} className="lc-input" inputMode="numeric" placeholder="sans limite" value={v.quota} disabled={ro} onChange={(ev) => set('quota', ev.target.value)} />
          </Champ>
        </div>
      </section>

      <section className="bs-tf__section" aria-labelledby={id('t-periode')}>
        <h3 className="bs-tf__titre" id={id('t-periode')}>Période de vente</h3>
        <span className="lc-label">Début</span>
        <div className="bs-tf__debuts" role="group" aria-label="Début de la vente">
          {DEBUTS.map((d) => (
            <button key={d.valeur} type="button" className={`bs-tf__debut${debut === d.valeur ? ' bs-tf__debut--on' : ''}`} aria-pressed={debut === d.valeur}
              disabled={ro} onClick={() => choisirDebut(d.valeur)}>{d.libelle}</button>
          ))}
        </div>
        {debut === 'date' ? dateHeure('vente_debut', 'Jour et heure du début', () => choisirDebut('mise')) : null}
        {debut === 'apres' ? (
          <div className="bs-tf__cascade">
            <p className="lc-hint bs-tf__cascade-aide">Les tarifs d’un même groupe s’ouvrent l’un après l’autre, par rang : le suivant prend le relais quand le précédent n’est plus en vente (épuisé, fini ou retiré).</p>
            {props.cascades.length > 0 ? (
              <div className="bs-tf__rejoindre" role="group" aria-label="Cascade à rejoindre">
                {props.cascades.map((c) => {
                  const on = v.cascade_groupe.trim() === c.groupe
                  return (
                    <button key={c.groupe} type="button" className={`bs-tf__debut${on ? ' bs-tf__debut--on' : ''}`} aria-pressed={on} disabled={ro}
                      onClick={() => props.onChange({ ...v, cascade_groupe: c.groupe, cascade_rang: on && v.cascade_rang ? v.cascade_rang : String(c.rang) })}>
                      Après « {c.noms[c.noms.length - 1]} »
                    </button>
                  )
                })}
              </div>
            ) : <p className="lc-hint">Aucun autre tarif de ce canal n’est dans une cascade : donnez un nom de groupe ci-dessous, puis le même aux tarifs qui suivront.</p>}
            <div className="bs-tf__grille">
              <Champ id={id('groupe')} label="Groupe de la cascade" erreur={e.cascade_groupe} aide="Les tarifs d’un même groupe s’ouvrent l’un après l’autre.">
                <input id={id('groupe')} className="lc-input" placeholder="sur-place" value={v.cascade_groupe} maxLength={32} disabled={ro} onChange={(ev) => set('cascade_groupe', ev.target.value)} />
              </Champ>
              <Champ id={id('rang')} label="Rang" erreur={e.cascade_rang} aide="1 s’ouvre d’abord. Échanger deux rangs passe par un rang libre.">
                <input id={id('rang')} className="lc-input" inputMode="numeric" value={v.cascade_rang} disabled={ro} onChange={(ev) => set('cascade_rang', ev.target.value)} />
              </Champ>
            </div>
            {v.vente_debut || dateApresOuverte
              ? dateHeure('vente_debut', 'Et pas avant le', () => { setDateApresOuverte(false); set('vente_debut', '') })
              : !ro ? (
                <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm bs-tf__ajout" onClick={() => setDateApresOuverte(true)}>
                  <Plus size={14} aria-hidden="true" /> Pas avant une date (facultatif)
                </button>
              ) : null}
          </div>
        ) : null}

        <span className="lc-label">Fin</span>
        {v.vente_fin || finOuverte
          ? dateHeure('vente_fin', 'Jour et heure de la fin', () => { setFinOuverte(false); set('vente_fin', '') })
          : !ro ? (
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm bs-tf__ajout" onClick={() => setFinOuverte(true)}>
              <Plus size={14} aria-hidden="true" /> Ajouter une fin de vente
            </button>
          ) : <span className="lc-hint">Aucune : il se vend jusqu’à la fin de la soirée.</span>}
      </section>

      <button type="button" className="bs-tf__plus" aria-expanded={avanceesVisibles} onClick={() => setAvancees(!avanceesVisibles)}>
        Options avancées <ChevronDown size={15} aria-hidden="true" className={avanceesVisibles ? 'bs-tf__chevron--haut' : undefined} />
      </button>

      {avanceesVisibles ? (
        <div className="bs-tf__options">
          <h3 className="bs-tf__titre">À la porte</h3>
          {carte({
            titre: 'Valable seulement avant une heure',
            desc: verrous.includes('ENTREE_AVANT_AVANCER') ? VERROU_LIBELLE['ENTREE_AVANT_AVANCER']! : v.canal === 'SUR_PLACE' ? 'Sans effet sur place : utilisez la fin de vente.' : 'L’entrée doit se faire avant cette heure de la nuit.',
            on: entreeOn,
            basculer: (on) => { setOuvertes({ ...ouvertes, entree: on }); if (!on) set('entree_avant', '') },
            children: (
              <Champ id={id('entree')} label="Heure limite d’entrée" erreur={e.entree_avant} aide="« 01:30 » tombe le lendemain.">
                <input id={id('entree')} className="lc-input bs-court" type="time" step={60} value={v.entree_avant} disabled={ro} onChange={(ev) => set('entree_avant', ev.target.value)} />
              </Champ>
            ),
          })}
          {props.salles.length > 1 ? carte({
            titre: 'Réservé à certaines salles',
            desc: verrous.includes('SALLES_RETRAIT') ? raison('SALLES_RETRAIT') : 'Sans choix, il vaut pour toutes les salles de la soirée. Affiché à la porte, pas contrôlé.',
            on: sallesOn,
            basculer: (on) => { setOuvertes({ ...ouvertes, salles: on }); if (!on) set('salles', []) },
            children: (
              <>
                <span className="bs-cases">
                  {props.salles.map((s) => (
                    <Checkbox key={s.code} label={s.nom} checked={v.salles.includes(s.code)} disabled={ro}
                      onChange={(on) => set('salles', on ? [...v.salles, s.code] : v.salles.filter((x) => x !== s.code))} />
                  ))}
                </span>
                {e.salles ? <span className="lc-helper lc-helper--error" role="alert">{e.salles}</span> : null}
              </>
            ),
          }) : null}
          {carte({
            titre: 'Consigne pour la porte',
            desc: 'Affichée au scan d’un billet, jamais pour une vente à la caisse.',
            on: consigneOn,
            basculer: (on) => { setOuvertes({ ...ouvertes, consigne: on }); if (!on) set('consigne_porte', '') },
            children: (
              <Champ id={id('consigne')} label="Consigne" erreur={e.consigne_porte} aide="80 caractères.">
                <input id={id('consigne')} className="lc-input" value={v.consigne_porte} maxLength={80} disabled={ro} placeholder="Vérifier la carte étudiante" onChange={(ev) => set('consigne_porte', ev.target.value)} />
              </Champ>
            ),
          })}

          <h3 className="bs-tf__titre">Vente</h3>
          {v.canal === 'SUR_PLACE' ? (
            <div className={`bs-tf__carte${v.exoneration ? ' bs-tf__carte--on' : ''}${e.exoneration ? ' bs-champ--erreur' : ''}`}>
              <SwitchRow checked={v.exoneration} onChange={(x) => set('exoneration', x)} disabled={ro || !!fige('exoneration')}
                title="Exonération" desc={fige('exoneration') ?? 'Entrée gratuite, motif demandé à la caisse. Toujours à 0 €, sans frais.'} />
              {e.exoneration ? <span className="lc-helper lc-helper--error" role="alert">{e.exoneration}</span> : null}
            </div>
          ) : null}
          <div className={`bs-tf__carte${v.cache ? ' bs-tf__carte--on' : ''}`}>
            <SwitchRow checked={v.cache} onChange={(x) => set('cache', x)} disabled={ro} title="Caché" desc="Enregistré, mais rien ne le lit encore." />
          </div>

          {props.complet ? (
            <>
              <h3 className="bs-tf__titre">Affichage</h3>
              <div className="bs-tf__grille">
                <Champ id={id('ordre')} label="Ordre d’affichage" erreur={e.ordre} aide="De 0 à 1 000 : le plus petit s’affiche en premier.">
                  <input id={id('ordre')} className="lc-input bs-court" inputMode="numeric" value={v.ordre} disabled={ro} onChange={(ev) => set('ordre', ev.target.value)} />
                </Champ>
              </div>
              <Champ id={id('desc')} label="Description" erreur={e.description} aide="500 caractères au plus.">
                <textarea id={id('desc')} className="lc-input bs-zone" rows={3} value={v.description} maxLength={500} disabled={ro} onChange={(ev) => set('description', ev.target.value)} />
              </Champ>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

